'use client';

import React, { createContext, useContext, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import DataroomErrorHandler from '@dataroom/error-handler';
import RestrictedRepository from '../infrastructure/RestrictedRepository';
import { ITransactionTypeGroupItem } from '../domain/vo/TransactionTypeGroupItem';

interface ITransactionTypeGroupsContext {
  isFetching: boolean,
  isEmptyCollection: boolean,
  collection: ITransactionTypeGroupItem[],
  activeGroup: ITransactionTypeGroupItem | null,
  defaultGroup: ITransactionTypeGroupItem | null,
  getTransactionTypeGroups: () => Promise<void>,
  setActiveGroup: React.Dispatch<React.SetStateAction<ITransactionTypeGroupItem | null>>,
}

const useTransactionTypeGroups = (): ITransactionTypeGroupsContext => {
  const container = getContainer();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isEmptyCollection, setIsEmptyCollection] = useState<boolean>(false);
  const [collection, setCollection] = useState<ITransactionTypeGroupItem[]>([]);
  const [activeGroup, setActiveGroup] = useState<ITransactionTypeGroupItem | null>(null);
  const [defaultGroup, setDefaultGroup] = useState<ITransactionTypeGroupItem | null>(null);

  const getTransactionTypeGroups = async (): Promise<void> => {
    setIsFetching(true);

    try {
      const restrictedRepository = container.get<RestrictedRepository>(RestrictedRepository);
      const groups = await restrictedRepository.getTransactionTypeGroups();

      setCollection(groups);
      setActiveGroup(groups?.[0]);
      setDefaultGroup(groups?.[0]);
      setIsEmptyCollection(!groups.length);
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
      setIsEmptyCollection(true);
    } finally {
      setIsFetching(false);
    }
  };

  return {
    isFetching,
    isEmptyCollection,
    collection,
    activeGroup,
    defaultGroup,
    getTransactionTypeGroups,
    setActiveGroup,
  };
};

export const TransactionTypeGroupsContext = createContext<ITransactionTypeGroupsContext | null>(null);

export const useTransactionTypeGroupsContext = () => {
  const context = useContext(TransactionTypeGroupsContext);
  if (!context) {
    throw new Error('useTransactionTypeGroupsContext must be used within a TransactionTypeGroupsContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const TransactionTypeGroupsContextProvider = ({ children }: IProps) => (
  <TransactionTypeGroupsContext.Provider value={ useTransactionTypeGroups() }>
    { children }
  </TransactionTypeGroupsContext.Provider>
);

export default TransactionTypeGroupsContextProvider;
